import { Controller } from '@hotwired/stimulus';
import { trackEvent } from '../analytics';
import { extensionPresent } from './helpers/extension_presence';
import { storeParams } from './helpers/extension_store';
import { STATUS, statusFor } from './helpers/undistractor_status';
import { tourRunning } from '../tours/helpers';
import UndistractorOptionStore from '../stores/undistractor_option_store';

// Gates are client-side: lite's HTML is one shared Cloudflare object and its visitors are signed out.
// The app runs the same gates, but localStorage is per origin, so lite and the app keep separate histories.
// Web-store policy: gate on usage, never sentiment — feedback sits beside the review link, not in front of it.

// Dated from the first visit here that saw the extension in this browser: nothing records an install date.
const MIN_INSTALLED_DAYS = 7;
// Distinct days with a switch-on made on a page carrying the card, today's included.
const MIN_SWITCH_ON_DAYS = 3;
const REVEAL_DELAY_MS = 1500;

// Days, not page loads: a refresh must not burn an ask.
const MAX_ASK_DAYS = 2;
const DISMISS_SUPPRESSION_DAYS = 90;
// Finite: never re-asking someone who complained is review gating.
const FEEDBACK_SUPPRESSION_DAYS = 90;

const STATE_KEY = 'unscreenReviewPrompt';
const MS_PER_DAY = 86400000;

const HIDDEN_CLASS = 'review-prompt-hidden';
const REPLACED_CLASS = 'know-your-tools-replaced';

export default class extends Controller {
  static targets = ['card', 'knowYourTools', 'ask', 'feedbackToggle', 'feedbackForm', 'back'];
  // Event names come from the view so PosthogEvents stays the single dictionary.
  static values = { surface: String, shownEvent: String, clickedEvent: String, dismissedEvent: String, focusAfterHideId: String };

  connect() {
    this.shown = false;
    this.state = this.#readState();
    this.store = UndistractorOptionStore.getInstance();
    this.unsubscribe = this.store.subscribeAll(() => this.#recordFirstSeen());
    // Real switches only: never the tour's demo toggles or a payload read back.
    this.onExtensionUpdate = (event) => this.#recordSwitch(event.detail);
    document.addEventListener('updateExtensionValues', this.onExtensionUpdate);
    // A failed save reverts through updateExtensionValues too, and a reverted switch-off reads as a switch-on.
    this.onSaveFailed = () => this.#cancelReveal();
    document.addEventListener('undistractor-settings-page-row:saveFailed', this.onSaveFailed);
    this.onFeedbackSent = (event) => this.#recordFeedback(event);
    document.addEventListener('feedback:sent', this.onFeedbackSent);
  }

  disconnect() {
    this.#cancelReveal();
    this.#stopWatchingView();
    this.unsubscribe?.();
    document.removeEventListener('updateExtensionValues', this.onExtensionUpdate);
    document.removeEventListener('undistractor-settings-page-row:saveFailed', this.onSaveFailed);
    document.removeEventListener('feedback:sent', this.onFeedbackSent);
  }

  // trackEvent, not a bare capture: a trip to the store is a conversion.
  review() {
    // The card stays clickable after the new tab opens; a second click would push click-through past 100%.
    if (this.state.clickedAt) return;

    this.#patchState({ clickedAt: Date.now() });
    if (!this.clickedEventValue) return;

    trackEvent(this.clickedEventValue, { surface: this.surfaceValue, ...storeParams() });
  }

  // Only the first opening counts; Back and a second look are free.
  feedback() {
    this.askTarget.hidden = true;
    this.feedbackFormTarget.hidden = false;
    this.feedbackFormTarget.querySelector('textarea')?.focus();
    if (this.feedbackOpened) return;

    this.feedbackOpened = true;
    this.#patchState({ feedbackAt: Date.now() });
    this.#captureDismissed('feedback');
  }

  back() {
    this.feedbackFormTarget.hidden = true;
    this.askTarget.hidden = false;
    this.feedbackToggleTarget.focus();
  }

  feedbackDismissed() {
    this.#hide();
  }

  dismiss() {
    this.#patchState({ dismissedAt: Date.now() });
    if (!this.feedbackOpened) this.#captureDismissed('not_now');
    this.#hide();
  }

  #captureDismissed(via) {
    if (!this.dismissedEventValue) return;

    window.posthog?.capture(this.dismissedEventValue, {
      surface: this.surfaceValue,
      via,
      ask_number: this.state.shownCount || 0,
    });
  }

  #recordFirstSeen() {
    if (!extensionPresent()) return;

    // Re-read, or another tab's earlier date would be overwritten.
    this.state = this.#readState();
    if (!this.state.firstSeenAt) this.#patchState({ firstSeenAt: Date.now() });
  }

  // Counted only once the delay passes: on-then-off is trying a tool, and switching a tool off from
  // Always-with-a-window passes through {active: true} on the way.
  #recordSwitch(updates) {
    if (!updates?.some((update) => update.active || update.schedule)) return this.#cancelReveal();
    if (this.revealTimer) return;

    this.revealTimer = setTimeout(() => {
      this.revealTimer = null;
      this.state = this.#readState();
      this.#recordFirstSeen();
      if (!this.#isToday(this.state.lastSwitchOnAt)) {
        this.#patchState({ switchOnDays: (this.state.switchOnDays || 0) + 1, lastSwitchOnAt: Date.now() });
      }
      // The tour's closing step asks for a real switch-on; the ask can wait for the next one.
      if (!tourRunning() && this.#eligible()) this.#show();
    }, REVEAL_DELAY_MS);
  }

  #cancelReveal() {
    clearTimeout(this.revealTimer);
    this.revealTimer = null;
  }

  #eligible() {
    if (this.shown || !extensionPresent()) return false;

    const { firstSeenAt, switchOnDays } = this.state;
    if (!firstSeenAt || Date.now() - firstSeenAt < MIN_INSTALLED_DAYS * MS_PER_DAY) return false;
    if ((switchOnDays || 0) < MIN_SWITCH_ON_DAYS) return false;

    return this.#askable();
  }

  #askable() {
    const { clickedAt, dismissedAt, feedbackAt, shownCount, lastShownAt } = this.state;
    if (clickedAt) return false;
    if (this.#within(dismissedAt, DISMISS_SUPPRESSION_DAYS)) return false;
    if (this.#within(feedbackAt, FEEDBACK_SUPPRESSION_DAYS)) return false;

    // Already counted today, so a reload re-shows the same ask rather than spending one.
    return (shownCount || 0) < MAX_ASK_DAYS || this.#isToday(lastShownAt);
  }

  #show() {
    // Below xl the card is display:none; keep the reveal for when the sidebar can show it.
    if (!this.cardTarget.getClientRects().length) return;
    // The app's right sidebar scrolls with the page: a switch far down the table would spend an ask-day unseen.
    if (!this.#inView()) return this.#showOnceInView();

    this.shown = true;
    this.cardTarget.classList.remove(HIDDEN_CLASS);
    if (this.hasKnowYourToolsTarget) this.knowYourToolsTarget.classList.add(REPLACED_CLASS);
    this.#recordShown();
  }

  #inView() {
    const { top, bottom } = this.element.getBoundingClientRect();
    return bottom >= 0 && top <= window.innerHeight;
  }

  #showOnceInView() {
    if (this.viewObserver) return;

    this.viewObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this.#stopWatchingView();
      this.state = this.#readState();
      if (!tourRunning() && this.#eligible()) this.#show();
    });
    this.viewObserver.observe(this.element);
  }

  #stopWatchingView() {
    this.viewObserver?.disconnect();
    this.viewObserver = null;
  }

  // shown stays true so a dismissal holds even when the localStorage write was refused.
  #hide() {
    const hadFocus = this.cardTarget.contains(document.activeElement);
    this.cardTarget.classList.add(HIDDEN_CLASS);
    if (this.hasKnowYourToolsTarget) this.knowYourToolsTarget.classList.remove(REPLACED_CLASS);
    // Lite's card is still mid-slide and the app's may start above the fold: scrolling to either would jump.
    if (hadFocus) document.getElementById(this.focusAfterHideIdValue)?.focus({ preventScroll: true });
  }

  // One event per ask-day, so its count is the denominator the click-through rate needs.
  #recordShown() {
    if (this.#isToday(this.state.lastShownAt)) return;

    const shownCount = (this.state.shownCount || 0) + 1;
    this.#patchState({ shownCount, lastShownAt: Date.now() });
    if (!this.shownEventValue) return;

    window.posthog?.capture(this.shownEventValue, {
      surface: this.surfaceValue,
      ask_number: shownCount,
      active_tools: this.store.getAll().filter((option) => statusFor(option) !== STATUS.INACTIVE).length,
      installed_days: Math.floor((Date.now() - this.state.firstSeenAt) / MS_PER_DAY),
      switch_on_days: this.state.switchOnDays,
    });
  }

  // From its own form the card holds the thank-you until feedbackDismissed; Back would lead to a spent ask.
  #recordFeedback(event) {
    this.#patchState({ feedbackAt: Date.now() });
    if (!this.shown) return;
    if (!this.cardTarget.contains(event.target)) return this.#hide();

    this.backTarget.hidden = true;
  }

  #within(timestamp, days) {
    return !!timestamp && Date.now() - timestamp < days * MS_PER_DAY;
  }

  #isToday(timestamp) {
    return !!timestamp && new Date(timestamp).toDateString() === new Date().toDateString();
  }

  // Storage throws when blocked; an unreadable state must mean "never asked", not a broken sidebar.
  #readState() {
    try {
      return JSON.parse(localStorage.getItem(STATE_KEY)) || {};
    } catch {
      return {};
    }
  }

  // Storage over the snapshot so another tab's writes survive; the snapshot stands in when it's blocked.
  #patchState(changes) {
    this.state = { ...this.state, ...this.#readState(), ...changes };
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(this.state));
    } catch {
      // See above.
    }
  }
}
